import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { fmtDate } from "@/lib/constants";
import { RECON_STATUS_STYLES, RECON_STATUSES, READINESS_STYLES, PARTNER_TYPES, RECON_SUBROLES } from "@/lib/reconConstants";
import { ClipboardCheck, Plus, Building2, Search, Users, ShieldCheck, Trash2, ArrowRight } from "lucide-react";
import { RecordActionsMenu, RecordBadges } from "@/components/RecordActions";
import { Switch } from "@/components/ui/switch";

const CARD_DEFS = [
  { key: "active_reviews", label: "Active Reviews" },
  { key: "awaiting_complyx", label: "Awaiting ComplyX" },
  { key: "awaiting_implementer", label: "Awaiting Implementer" },
  { key: "awaiting_client", label: "Awaiting Client" },
  { key: "ready_for_final", label: "Ready for Final Review" },
  { key: "completed", label: "Completed Reviews" },
];

export default function ReconDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [rows, setRows] = useState([]);
  const [me, setMe] = useState(null);
  const [partners, setPartners] = useState([]);
  const [filters, setFilters] = useState({ status: "all", partner_id: "all", q: "", include_test: false, include_archived: false });
  const [showPartners, setShowPartners] = useState(false);
  const [showRoles, setShowRoles] = useState(false);

  const load = () => {
    const sp = {};
    if (filters.include_test) sp.include_test = "yes";
    if (filters.include_archived) sp.include_archived = "yes";
    api.get("/recon/stats", { params: sp }).then((r) => setStats(r.data)).catch(() => {});
    api.get("/recon/partners", { params: filters.include_archived ? { include_archived: "yes" } : {} }).then((r) => setPartners(r.data)).catch(() => {});
    const params = { ...sp };
    if (filters.status !== "all") params.status = filters.status;
    if (filters.partner_id !== "all") params.partner_id = filters.partner_id;
    if (filters.q) params.q = filters.q;
    api.get("/recon/reconciliations", { params }).then((r) => setRows(r.data)).catch(() => {});
  };
  useEffect(() => { api.get("/recon/me").then((r) => setMe(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  const caps = me?.caps || {};

  return (
    <div>
      <header className="bg-navy px-6 md:px-8 py-6">
        <p className="overline text-gold mb-1.5">Operations · Admin Only</p>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl md:text-3xl font-bold text-white tracking-tight leading-none flex items-center gap-2">
              <ClipboardCheck className="h-6 w-6 text-gold" aria-hidden="true" /> Implementation Reconciliation
            </h1>
            <p className="text-slate-300 text-sm mt-2">Compare a customer SSP against an implementation partner report, control by control. AI-assisted — findings stay "AI Suggested" until a ComplyX admin approves them.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" data-testid="recon-manage-partners" onClick={() => setShowPartners(true)} className="bg-white/5 border-white/20 text-white hover:bg-white/10">
              <Building2 className="h-4 w-4 mr-2" /> Partners
            </Button>
            {caps.manage_roles && (
              <Button variant="outline" data-testid="recon-manage-roles" onClick={() => setShowRoles(true)} className="bg-white/5 border-white/20 text-white hover:bg-white/10">
                <Users className="h-4 w-4 mr-2" /> Roles
              </Button>
            )}
            {caps.create && (
              <Button data-testid="recon-new-button" onClick={() => navigate("/operations/reconciliation/new")} className="bg-gold text-navy hover:bg-gold-dark font-semibold">
                <Plus className="h-4 w-4 mr-2" /> New Reconciliation
              </Button>
            )}
          </div>
        </div>
      </header>

      <div className="p-6 md:p-8 space-y-6">
        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="recon-summary-cards">
          {CARD_DEFS.map((c) => (
            <div key={c.key} className="rounded-md border border-border bg-card p-4" data-testid={`recon-card-${c.key}`}>
              <div className="text-3xl font-display font-bold text-navy">{stats[c.key] ?? 0}</div>
              <div className="text-xs text-muted-foreground mt-1">{c.label}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="rounded-md border border-border bg-card p-4 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px] space-y-1.5">
            <Label className="text-xs">Search</Label>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
              <Input data-testid="recon-search" className="pl-8" placeholder="Customer or reconciliation title" value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Status</Label>
            <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}>
              <SelectTrigger className="w-[180px]" data-testid="recon-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {RECON_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Implementation Partner</Label>
            <Select value={filters.partner_id} onValueChange={(v) => setFilters((f) => ({ ...f, partner_id: v }))}>
              <SelectTrigger className="w-[200px]" data-testid="recon-filter-partner"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All partners</SelectItem>
                {partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end gap-4 pb-1.5">
            <label className="flex items-center gap-2 text-xs text-navy cursor-pointer">
              <Switch checked={filters.include_test} onCheckedChange={(v) => setFilters((f) => ({ ...f, include_test: v }))} data-testid="recon-include-test" /> Include Test Data
            </label>
            <label className="flex items-center gap-2 text-xs text-navy cursor-pointer">
              <Switch checked={filters.include_archived} onCheckedChange={(v) => setFilters((f) => ({ ...f, include_archived: v }))} data-testid="recon-include-archived" /> Show Archived
            </label>
          </div>
        </div>

        {/* Table */}
        <div className="rounded-md border border-border bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-border text-left">
                  {["Customer", "Implementation Partner", "SSP Ver.", "Report Ver.", "Technical", "Evidence", "Open Actions", "Reviewer", "Last Updated", "Status", ""].map((h) => (
                    <th key={h} className="px-3 py-3 font-semibold text-navy text-xs uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={11} className="px-4 py-12 text-center text-muted-foreground" data-testid="recon-empty">No reconciliations yet. Click "New Reconciliation" to start.</td></tr>
                ) : rows.map((r) => (
                  <tr key={r.id} data-testid={`recon-row-${r.id}`} onClick={() => navigate(`/operations/reconciliation/${r.id}`)} className="border-b border-border last:border-0 hover:bg-slate-50 cursor-pointer">
                    <td className="px-3 py-3 font-medium text-navy">
                      <div className="flex items-center gap-2">{r.customer_name}<RecordBadges classification={r.classification} lifecycleState={r.lifecycle_state} /></div>
                    </td>
                    <td className="px-3 py-3 text-xs">{r.implementation_partner_name || "—"}</td>
                    <td className="px-3 py-3 text-xs">{r.ssp_version || "—"}</td>
                    <td className="px-3 py-3 text-xs">{r.report_version || "—"}</td>
                    <td className="px-3 py-3 text-xs">{r.scores?.technical != null ? `${r.scores.technical}%` : "—"}</td>
                    <td className="px-3 py-3 text-xs">{r.scores?.evidence != null ? `${r.scores.evidence}%` : "—"}</td>
                    <td className="px-3 py-3 text-xs">{r.open_actions ?? 0}</td>
                    <td className="px-3 py-3 text-xs text-muted-foreground">{r.assigned_reviewer || "—"}</td>
                    <td className="px-3 py-3 text-xs text-muted-foreground whitespace-nowrap">{fmtDate(r.updated_at)}</td>
                    <td className="px-3 py-3 whitespace-nowrap"><span className={`text-[11px] px-2 py-0.5 rounded border ${RECON_STATUS_STYLES[r.status] || ""}`}>{r.status}</span></td>
                    <td className="px-3 py-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        <ArrowRight className="h-4 w-4 text-muted-foreground cursor-pointer" onClick={() => navigate(`/operations/reconciliation/${r.id}`)} />
                        <RecordActionsMenu type="reconciliation" id={r.id} onChanged={load} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <PartnersDialog open={showPartners} onOpenChange={setShowPartners} partners={partners} canDelete={caps.delete} onChange={load} />
      {caps.manage_roles && <RolesDialog open={showRoles} onOpenChange={setShowRoles} />}
    </div>
  );
}

function PartnersDialog({ open, onOpenChange, partners, canDelete, onChange }) {
  const empty = { name: "", primary_contact: "", email: "", phone: "", website: "", partner_type: "Other", active: true, notes: "" };
  const [form, setForm] = useState(empty);
  const save = async () => {
    if (!form.name.trim()) { toast.error("Partner name is required"); return; }
    try { await api.post("/recon/partners", form); toast.success("Partner added"); setForm(empty); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => { try { await api.delete(`/recon/partners/${id}`); toast.success("Partner removed"); onChange(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="partners-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-navy">Implementation Partners</DialogTitle>
          <DialogDescription>Reusable partner list used across reconciliations and action assignments.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 max-h-[300px] overflow-y-auto">
          {partners.length === 0 ? <p className="text-sm text-muted-foreground">No partners yet.</p> : partners.map((p) => (
            <div key={p.id} className="flex items-center justify-between rounded border border-border p-3">
              <div>
                <div className="font-medium text-navy text-sm flex items-center gap-2">{p.name} <span className="text-[11px] text-muted-foreground">· {p.partner_type}</span><RecordBadges classification={p.classification} lifecycleState={p.lifecycle_state} /></div>
                <div className="text-xs text-muted-foreground">{p.primary_contact} {p.email && `· ${p.email}`}</div>
              </div>
              <RecordActionsMenu type="partner" id={p.id} onChanged={onChange} />
            </div>
          ))}
        </div>
        <div className="border-t border-border pt-4 grid sm:grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label className="text-xs">Partner name *</Label><Input data-testid="partner-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div className="space-y-1.5"><Label className="text-xs">Partner type</Label>
            <Select value={form.partner_type} onValueChange={(v) => setForm({ ...form, partner_type: v })}>
              <SelectTrigger data-testid="partner-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{PARTNER_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Primary contact</Label><Input value={form.primary_contact} onChange={(e) => setForm({ ...form, primary_contact: e.target.value })} /></div>
          <div className="space-y-1.5"><Label className="text-xs">Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
        </div>
        <DialogFooter><Button data-testid="partner-save" onClick={save} className="bg-navy text-white hover:bg-navy-700">Add Partner</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RolesDialog({ open, onOpenChange }) {
  const [users, setUsers] = useState([]);
  const load = () => api.get("/recon/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { if (open) load(); }, [open]);
  const setRole = async (uid, role) => { try { await api.put(`/recon/users/${uid}/recon-role`, { recon_role: role }); toast.success("Role updated"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="roles-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-navy">Reconciliation Roles</DialogTitle>
          <DialogDescription>Assign reconciliation sub-roles to admin users. Sales users have no access to this module.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[360px] overflow-y-auto">
          {users.map((u) => (
            <div key={u.user_id} className="flex items-center justify-between rounded border border-border p-3" data-testid={`role-row-${u.user_id}`}>
              <div><div className="font-medium text-navy text-sm">{u.name || u.email}</div><div className="text-xs text-muted-foreground">{u.email}</div></div>
              <Select value={u.recon_role} onValueChange={(v) => setRole(u.user_id, v)}>
                <SelectTrigger className="w-[200px]" data-testid={`role-select-${u.user_id}`}><SelectValue /></SelectTrigger>
                <SelectContent>{RECON_SUBROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
